import { GridColDef, GridFilterModel, GridValueFormatterParams, GridValueGetterParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { DataTable, DataTableProps } from 'components';
import dayjs from 'dayjs';
import { CustomFieldDto, CustomFieldType, EntityType, FindIndividualsQuery, Individual as IndividualEntity, SystemFieldId, useFindSectionsQuery, CustomFieldEntityType as CustomFieldEntityTypeEntity } from 'gql';
import orderBy from 'lodash/orderBy';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCustomFieldsSearchUtils } from '../../../../utils/dataGridSearch/customFieldsSearch';
import { notEmpty } from '../../../../utils/typescriptUtils';
import { IndividualActions } from '../IndividualActions/IndividualActions';

type Individual = Pick<IndividualEntity, 'id' | 'birthday' | 'nickName' | 'gender' | 'customFieldValues'>;

type CustomFieldEntityType = Pick<CustomFieldEntityTypeEntity, 'entityType' | 'customFieldId'> & {
  customFieldDto: Pick<CustomFieldDto, 'customFieldType' | 'systemFieldId' | 'allowMultipleValues'>;
};

interface Props extends Omit<DataTableProps, 'rows' | 'columns'> {
  storageKey: string;
  onFilter?: (filter: GridFilterModel) => void,
  onColumnsChange?: (columns: GridColDef[]) => void,
  individuals: NonNullable<NonNullable<Pick<FindIndividualsQuery, 'searchIndividuals'>['searchIndividuals']>['items']>;
  onEdit?: (individual: Individual) => void;
  isFetching?: boolean;
}

const valueGetterByFieldType = (p: CustomFieldEntityType) => ({ row: { customFieldValues } }: GridValueGetterParams<Individual>) => {
  if (!customFieldValues) {
    return '';
  }

  const fieldType = p.customFieldDto.customFieldType;

  if (fieldType === CustomFieldType.Date) {
    return dayjs(customFieldValues.find(q => q.fieldId === p.customFieldId)?.dateValue).toDate();
  } else {
    return customFieldValues.find(q => q.fieldId === p.customFieldId)?.displayString;
  }
};

const valueFormatterFunction = (p: CustomFieldEntityType) => {
  const systemFieldId = p.customFieldDto.systemFieldId;
  if (systemFieldId === SystemFieldId.Birthday) {
    return ({ value }: GridValueFormatterParams<Date>) => {
      return dayjs().diff(dayjs(value), 'year');
    };
  }
};

export const IndividualsTable: React.FC<Props> = ({ storageKey, individuals, onEdit, onFilter, onColumnsChange, isFetching, ...dataTableProps }) => {
  const { data: sections } = useFindSectionsQuery({ filter: { entityType: { eq: EntityType.Individual } } });
  const { formatMessage } = useIntl();
  const { columnMinWidth, customFieldTypeToColumnType, getOperatorsByCustomField } = useCustomFieldsSearchUtils();
  const wasGridStateRestored = React.useRef(false);
  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialStatePro>({});

  const columns = useMemo(() => {
    const fieldIds = new Set<number>();
    const visibilityConfig: Record<string, boolean> = {};
    const columnsToDisplay: GridColDef<Individual>[] =
      orderBy(sections?.sections.flatMap(p => p.fieldEntityTypes), p => p.order.toString())
        .map<GridColDef<Individual> | null>(p => {
          if (fieldIds.has(p.customFieldId)) {
            return null;
          }

          fieldIds.add(p.customFieldId);

          if (![SystemFieldId.Gender, SystemFieldId.NickName, SystemFieldId.Birthday].includes(p.customFieldDto.systemFieldId as SystemFieldId)) {
            visibilityConfig[`${p.entityType.toString()};${p.customFieldId.toString()}`] = false;
          }

          return {
            field: `${p.entityType.toString()};${p.customFieldId.toString()}`,
            type: customFieldTypeToColumnType[p.customFieldDto.customFieldType],
            sortable: false, // disable client side sorting which results in bad UX when combined with server side pagination
            filterOperators: getOperatorsByCustomField(p.customFieldDto),
            hideable: !(p.customFieldDto.systemFieldId === SystemFieldId.NickName),
            headerName: p.customFieldDto.name,
            flex: 1,
            minWidth: columnMinWidth[p.customFieldDto.customFieldType],
            valueGetter: valueGetterByFieldType(p),
            valueFormatter: valueFormatterFunction(p),
          };
        }).filter(notEmpty) ?? [];

    columnsToDisplay.push({
      field: formatMessage({ id: 'Actions' }),
      headerName: '',
      sortable: false,
      filterable: false,
      hideable: false,
      disableExport: true,
      disableColumnMenu: true,
      align: 'right',
      renderCell: (params) => (
        <IndividualActions
          individual={params.row}
          onEdit={onEdit ? () => onEdit(params.row) : undefined}
        />
      )
    });

    setInitialState({
      sorting: {
        sortModel: []
      },
      columns: {
        columnVisibilityModel: visibilityConfig
      }
    });

    return columnsToDisplay;
  }, [columnMinWidth, customFieldTypeToColumnType, formatMessage, getOperatorsByCustomField, onEdit, sections?.sections]);

  const prevColumnFieldsRef = useRef<string>('');

  useEffect(() => {
    const currentFields = columns.map(c => c.field).join(',');
    if (columns.length > 0 && currentFields !== prevColumnFieldsRef.current) {
      prevColumnFieldsRef.current = currentFields;
      onColumnsChange?.(columns);
    }
  }, [columns, onColumnsChange]);

  useEffect(() => {
    if (!wasGridStateRestored.current) {
      const stateJSON = localStorage.getItem(storageKey);
      if (stateJSON) {
        const state: GridInitialStatePro = JSON.parse(stateJSON);
        if (state.columns) state.columns.orderedFields = undefined;
        // Remove pagination state to prevent overwriting usePersistedPageSize
        delete state.pagination;
        apiRef.current.restoreState({ ...state, preferencePanel: { open: false } });
      } else {
        apiRef.current.restoreState(initialState);
      }

      wasGridStateRestored.current = true;
    }
  }, [apiRef, initialState, storageKey]);

  const saveTableState = useCallback(() => {
    const state = apiRef.current.exportState();
    localStorage.setItem(storageKey, JSON.stringify(state));
  }, [apiRef, storageKey]);

  const onFilterChange = React.useCallback((filterModel: GridFilterModel) => {
    if (onFilter != null) {
      onFilter(filterModel);
      saveTableState();
    }
  }, [onFilter, saveTableState]);

  return (
    <DataTable
      apiRef={apiRef}
      columns={columns}
      rows={individuals}
      isFetching={isFetching}
      noDataMessage={formatMessage({ id: 'There are no individuals yet' })}
      noSearchResultsMessage={formatMessage({ id: 'No individual found' })}
      rowHeight={64}
      keepNonExistentRowsSelected={true}
      {...dataTableProps}
      onColumnVisibilityModelChange={(model, details) => {
        saveTableState();
        dataTableProps.onColumnVisibilityModelChange?.(model, details);
      }}
      onFilterModelChange={onFilterChange}
      onSortModelChange={saveTableState}
    />
  );
};